import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Link2, UserCheck,
  Lock, Users, KeyRound, FileSearch, Archive, EyeOff, Layers,
  Compass, ClipboardCheck, Shield, Bell, Briefcase, Building2 } from "lucide-react";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import DecisionRecordMockup from "@/components/mockups/DecisionRecordMockup";
import { TESTIDS } from "@/lib/testIds";
import { setPathway } from "@/lib/pathway";

export default function Landing() {
  useEffect(() => {
    document.title = "ImpactVest | Investment Decision Continuity";
  }, []);

  return (
    <div className="relative">
      <Header />
      <main>
        <Hero />
        <ImmediateOutcome />
        <BusinessProblem />
        <ProductJourney />
        <EvidenceProof />
        <DecisionPassportSignature />
        <Security />
        <LiveUseBand />
      </main>
      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------------- HERO */

function Hero() {
  const ref = useRef(null);
  const [parallax, setParallax] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const onScroll = () => setParallax(Math.min(400, Math.max(0, window.scrollY)) * 0.04);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid={TESTIDS.hero.root}
      className="relative pt-28 md:pt-36 pb-24 md:pb-32 overflow-hidden iv-anchor"
    >
      <div className="grain" aria-hidden />
      <div className="iv-container relative">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="col-span-12 lg:col-span-6">
            <div className="iv-eyebrow mb-8">IMPACTVEST · Investment Decision Continuity</div>
            <h1
              data-testid={TESTIDS.hero.headline}
              className="font-serif-display text-[10.5vw] sm:text-[8vw] md:text-[5.4vw] lg:text-[3.8rem] xl:text-[4.3rem] leading-[1.02] tracking-[-0.02em] text-iv-fg"
            >
              Build investment conviction that endures.
            </h1>
            <p className="mt-7 text-[17px] md:text-[18px] leading-relaxed text-iv-fg-2 max-w-xl">
              ImpactVest is the evidence layer between the companies making sustainability
              and impact claims and the investors evaluating them — one intelligence engine,
              two clearly separate workflows.
            </p>

            {/* Above-the-fold pathway selector — two audiences ImpactVest serves.
                Each choice reads as an audience, not a generic CTA. */}
            <div
              data-testid={TESTIDS.pathway.heroChoice}
              className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl"
            >
              <Link
                to="/investors"
                data-testid={TESTIDS.pathway.heroInvestorCta}
                onClick={() => setPathway("investor")}
                aria-label="For investors — Evaluate an opportunity"
                className="group iv-path-card"
              >
                <div className="flex items-center gap-2 iv-path-eyebrow">
                  <Briefcase size={13} strokeWidth={1.7} />
                  For investors
                </div>
                <div className="iv-path-title">Evaluate an opportunity</div>
                <div className="iv-path-sub">Examine the evidence. Preserve the reasoning behind the decision.</div>
                <div className="iv-path-arrow">
                  <ArrowRight size={16} strokeWidth={1.8} />
                </div>
              </Link>
              <Link
                to="/companies"
                data-testid={TESTIDS.pathway.heroCompanyCta}
                onClick={() => setPathway("company")}
                aria-label="For companies — Build an evidence pack"
                className="group iv-path-card"
              >
                <div className="flex items-center gap-2 iv-path-eyebrow">
                  <Building2 size={13} strokeWidth={1.7} />
                  For companies
                </div>
                <div className="iv-path-title">Build an evidence pack</div>
                <div className="iv-path-sub">Substantiate your claims. Prepare for investor diligence.</div>
                <div className="iv-path-arrow">
                  <ArrowUpRight size={16} strokeWidth={1.8} />
                </div>
              </Link>
            </div>

            <p className="mt-7 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
              One product · One evidence engine · Two audience-specific journeys
            </p>
          </div>

          <div
            className="col-span-12 lg:col-span-6 relative"
            style={{ transform: `translateY(${-parallax}px)`, transition: "transform 0.5s cubic-bezier(0.16,1,0.3,1)" }}
          >
            <PassportPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

/* Minimal, on-brand hero preview — a compact Decision Passport card. */
function PassportPreview() {
  return (
    <div className="iv-canvas rounded-[3px] w-full" data-testid="hero-decision-record-mockup">
      <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--ivc-line)]">
        <div className="flex items-center gap-3">
          <span className="iv-ev-dot iv-ev-dot--verified" />
          <span className="font-mono text-[11px] tracking-[0.30em] uppercase text-[var(--ivc-gold-2)]">Decision Passport</span>
        </div>
        <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">
          Illustrative
        </span>
      </div>

      <div className="px-8 md:px-10 pt-9 pb-6">
        <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)]">Opportunity</div>
        <h4 className="font-serif-display text-[30px] md:text-[36px] leading-[1.05] tracking-tight mt-2 text-[var(--ivc-ivory)]">
          Meridia Groundwater — €20M
        </h4>

        <div className="mt-8 border-l-2 border-[var(--ivc-forest)] pl-6 py-2 bg-[linear-gradient(90deg,rgba(107,178,140,0.07),transparent_70%)]">
          <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-forest)]">
            Original thesis
          </div>
          <div className="font-serif-display text-[19px] md:text-[21px] leading-tight mt-2 text-[var(--ivc-ivory)]">
            Distributed groundwater intelligence, contracted anchor revenue and third-party verified impact.
          </div>
        </div>
      </div>

      <div className="px-8 md:px-10 pb-6">
        <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)] mb-3">
          What must remain true
        </div>
        <ul className="space-y-2">
          {[
            "Anchor customer coverage remains ≥ 60% of forward revenue.",
            "Hardware gross margin stays within the independent 34–37% band.",
            "Quarterly reporting delivered within 45 days of quarter-end.",
          ].map((s, i) => (
            <li key={s} className="flex items-start gap-3">
              <span className="font-mono text-[10px] tracking-[0.20em] text-[var(--ivc-gold-2)] mt-1.5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[14.5px] leading-snug text-[var(--ivc-ivory)]">{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="px-8 md:px-10 pb-8 border-t border-[var(--ivc-line-strong)] pt-6">
        <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)] mb-3">
          Reconsideration triggers
        </div>
        <ul className="space-y-2">
          {[
            "Top-three tenant concentration above 40% at 12-month checkpoint.",
            "Independent BOM confirms hardware margin below 30% for two quarters.",
          ].map((s) => (
            <li key={s} className="flex items-start gap-3">
              <span className="iv-ev-dot iv-ev-dot--review mt-1.5" />
              <span className="text-[13.5px] leading-snug text-[var(--ivc-ivory-2)]">{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------------------------------------------------- IMMEDIATE OUTCOME */

const OUTCOMES = [
  { icon: Compass, title: "Evaluate the opportunity",
    body: "Bring opportunity materials, financial analysis, ESG and impact evidence, and relevant jurisdictional context into a structured review." },
  { icon: ClipboardCheck, title: "Form a clear decision",
    body: "Connect the investment thesis, assumptions, supporting evidence and material market context in one transparent record." },
  { icon: Bell, title: "Strengthen conviction over time",
    body: "Revisit the decision as companies, markets, regulations and material conditions evolve." },
];

function ImmediateOutcome() {
  return (
    <section data-testid="section-outcome" className="relative py-24 md:py-32 border-t border-iv-line">
      <div className="iv-container">
        <div className="iv-eyebrow mb-4">From opportunity to enduring investment intelligence.</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mt-10">
          {OUTCOMES.map((o, i) => {
            const Icon = o.icon;
            return (
              <Reveal key={o.title} delay={i * 0.08} className="pt-8 border-t-2 border-[var(--iv-line-strong)]">
                <span className="text-iv-green"><Icon size={26} strokeWidth={1.6} /></span>
                <h3 className="font-serif-display text-[28px] md:text-[34px] leading-tight text-iv-ink mt-4">{o.title}</h3>
                <p className="text-[15.5px] leading-relaxed text-[var(--iv-fg-2)] mt-3 max-w-sm">{o.body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------ BUSINESS PROBLEM */

function BusinessProblem() {
  return (
    <section data-testid="section-problem" className="relative py-20 md:py-28 border-t border-iv-line">
      <div className="iv-container">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 lg:col-span-6">
            <div className="iv-eyebrow mb-8">Why decision continuity</div>
            <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight text-iv-fg">
              The decision should not disappear<br className="hidden md:block" /> once the investment is approved.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <p className="text-[16px] leading-relaxed text-iv-fg-2 max-w-xl">
              Investment teams often preserve the documents and the final recommendation, but lose the
              reasoning between them: the decisive evidence, unresolved questions, approval conditions
              and assumptions that must remain true.
            </p>
            <p className="text-[16px] leading-relaxed text-iv-fg-2 max-w-xl mt-4">
              ImpactVest carries that judgment forward — so the Office can understand not only what it
              decided, but why, and when the decision deserves another look.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------- PRODUCT JOURNEY */

const JOURNEY = [
  { n: "01", label: "Review",
    body: "See the issues that matter, the evidence behind them and the questions still requiring judgment." },
  { n: "02", label: "Decide",
    body: "Record the recommendation, conditions, dissent and authorized approval." },
  { n: "03", label: "Preserve",
    body: "Create a durable record of what the Office believed when the decision was made." },
  { n: "04", label: "Revisit",
    body: "Carry the thesis, assumptions and warning signs into ongoing monitoring." },
];

function ProductJourney() {
  return (
    <section id="how" data-testid={TESTIDS.sections.platform} className="relative py-24 md:py-32 border-t border-iv-line">
      <div className="iv-container">
        <div className="iv-eyebrow mb-10 md:mb-14">Review → Decide → Preserve → Revisit</div>
        <div className="border-t border-iv-line">
          {JOURNEY.map((s, i) => (
            <Reveal
              key={s.n}
              delay={i * 0.05}
              className="grid grid-cols-12 gap-4 md:gap-10 py-8 md:py-10 border-b border-iv-line items-baseline"
            >
              <div className="col-span-12 md:col-span-2 iv-chapter-number !text-[4rem] md:!text-[5rem]">{s.n}</div>
              <h3 className="col-span-12 md:col-span-3 font-serif-display text-[28px] md:text-[36px] leading-tight text-iv-fg">
                {s.label}
              </h3>
              <p className="col-span-12 md:col-span-7 text-[15px] leading-relaxed text-iv-fg-2">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------- DECISION PASSPORT SIGNATURE */

const PASSPORT_CONTENTS = [
  "Opportunity and decision",
  "Original investment thesis",
  "Decisive supporting evidence",
  "Material uncertainties",
  "Approval conditions",
  "Dissenting or alternative views",
  "What must remain true",
  "Reconsideration triggers",
  "Named owners",
  "Next review date",
  "Exact approved version and approval history",
];

function DecisionPassportSignature() {
  return (
    <section
      id="passport"
      data-testid="section-passport"
      className="iv-band-immersive relative pt-32 md:pt-44 pb-32 md:pb-44 overflow-hidden iv-anchor"
    >
      <div className="iv-container relative">
        <div className="iv-canvas-eyebrow mb-10 md:mb-14">The signature output</div>
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 lg:col-span-6">
            <h2 className="font-serif-display text-[40px] md:text-[58px] leading-[1.02] tracking-tight text-[var(--ivc-ivory)]">
              Every investment gains a<br/>Decision Passport.
            </h2>
            <p className="mt-8 text-[16px] leading-relaxed text-[var(--ivc-ivory-2)] max-w-xl">
              A Decision Passport captures the complete reasoning behind an investment — connecting the
              thesis, evidence, assumptions, relevant market context, material considerations and future
              review signals in one enduring record.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <div className="border-t border-[var(--ivc-line-strong)]">
              {PASSPORT_CONTENTS.map((c, i) => (
                <div key={c} className="grid grid-cols-[52px_1fr] gap-4 items-baseline py-3.5 border-b border-[var(--ivc-line)]">
                  <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-gold-2)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif-display text-[17px] leading-snug text-[var(--ivc-ivory)]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const PASSPORT_CONTENTS_UNUSED = null;  // legacy sentinel

/* ---------------------------------------------------------- EVIDENCE PROOF */

function EvidenceProof() {
  return (
    <section id="evidence" data-testid={TESTIDS.sections.record} className="relative py-24 md:py-32 border-t border-iv-line iv-anchor">
      <div className="iv-container">
        <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="iv-eyebrow mb-6">Evidence Map</div>
            <h2 className="font-serif-display text-[36px] md:text-[48px] leading-[1.03] tracking-tight text-iv-fg">
              Private evidence, interpreted in the context of the markets that shape it.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[15.5px] leading-relaxed text-iv-fg-2 max-w-md">
              ImpactVest connects each material conclusion to its underlying source and identifies the
              jurisdictions relevant to the opportunity — helping teams preserve market context,
              distinguish evidence from interpretation and recognise where further external or
              specialist review is required.
            </p>
          </div>
        </div>
        <DecisionRecordMockup />

        {/* Discreet capability row */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { t: "Jurisdiction-aware context",
              b: "Identify the jurisdictions relevant to an opportunity based on the uploaded materials." },
            { t: "Evidence across categories",
              b: "Work across financial, ESG and impact evidence while preserving the distinctions that matter." },
            { t: "Transparent provenance",
              b: "Distinguish user-supplied evidence, AI interpretation and matters requiring specialist review." },
            { t: "Responsive stewardship",
              b: "Recognise where further external or specialist review is required as conditions evolve." },
          ].map((c) => (
            <div key={c.t} className="border-t border-iv-line pt-5">
              <div className="font-serif-display text-[18px] leading-tight text-iv-fg">{c.t}</div>
              <p className="text-[13.5px] leading-relaxed text-iv-fg-2 mt-2">{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- SECURITY */

// Claims-to-code matrix (WP-G product-truth audit, 3 Aug 2026):
// * `Private, segregated ... workspace` — verified: every /test/* API path
//   filters by `user_id`, tenant-isolation regression covered in
//   `test_uploads_iteration6.py` and cross-tenant tests in
//   `test_evidence_integrity_p0.py`.
// * `Source-level traceability` — verified: passport signature covers
//   documents + citations + excerpts + resolution + assignment fields.
// * `Human approval before a decision becomes official` — verified:
//   WP-B revision endpoints require explicit PATCH into approved/user_edited;
//   Passport version snapshots record `released_by` + `released_at`.
// * `Customer info not used to train shared models` — verified at the
//   ImpactVest software layer; kept as a Privacy Notice commitment
//   subject to processor-level qualification.
// Items moved to "Planned institutional controls" until WP-INST/WP-J ship:
//   - Multi-user RBAC / granular permissions
//   - Organization-scoped multi-user access
//   - Complete activity + decision history at the organisation level
//   - Automated retention / deletion controls (currently manual)
const COMMITMENTS = [
  { icon: Layers,     label: "Private, single-user workspaces per registered account" },
  { icon: FileSearch, label: "Source-level traceability: every claim carries its citation" },
  { icon: UserCheck,  label: "Human approval before a suggestion becomes an approved claim revision" },
  { icon: Lock,       label: "Watermarks on every test document; secure sign-in links limit access to your own workspace" },
  { icon: EyeOff,     label: "Your data is not used to train shared LLM models by ImpactVest software" },
];

// Institutional controls that are in scope for WP-INST / WP-J and NOT yet
// shipped. Kept visible on the site as roadmap so the pilot commitments
// remain honest and internally consistent with the Privacy Notice.
const PLANNED_INSTITUTIONAL_COMMITMENTS = [
  { icon: Users,      label: "Multi-user organisation workspaces with role-based permissions" },
  { icon: KeyRound,   label: "Granular permissions across viewers / analysts / approvers" },
  { icon: Archive,    label: "Complete organisation-level activity and decision history" },
  { icon: Lock,       label: "Automated retention & deletion controls (deletion requests are handled manually today)" },
];

function Security() {
  return (
    <section
      id="security"
      data-testid={TESTIDS.sections.security}
      className="relative py-24 md:py-32 border-t border-iv-line bg-iv-bg-2 iv-anchor"
    >
      <div className="iv-container">
        <div className="grid grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="col-span-12 lg:col-span-4">
            <div className="iv-eyebrow mb-6">Confidentiality</div>
            <h2 className="font-serif-display text-[34px] md:text-[42px] leading-[1.05] tracking-tight text-iv-fg">
              Confidential intelligence within your organisation.
            </h2>
            <p className="text-[14px] text-iv-fg-2 mt-5 leading-relaxed max-w-sm">
              ImpactVest is designed for sensitive investment work, with opportunity evidence, analysis
              and decision records kept within each user&rsquo;s protected workspace. The current pilot is
              single-user; multi-user organisation workspaces with colleague invitations are a
              post-audit workstream.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <div className="iv-eyebrow mb-4">Current pilot commitments</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 border-t border-iv-line">
              {COMMITMENTS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <li
                    key={c.label}
                    className={`flex items-start gap-4 px-1 py-5 border-b border-iv-line ${
                      i % 2 === 0 ? "sm:border-r sm:pr-8" : "sm:pl-8"
                    }`}
                  >
                    <span className="mt-0.5 text-iv-gold">
                      <Icon size={16} strokeWidth={1.4} />
                    </span>
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-iv-fg-3">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="text-[14.5px] text-iv-fg leading-snug mt-1">{c.label}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="iv-eyebrow mt-10 mb-4">Planned institutional controls · not yet shipped</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 border-t border-iv-line" data-testid="planned-institutional-list">
              {PLANNED_INSTITUTIONAL_COMMITMENTS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <li key={c.label}
                    className={`flex items-start gap-4 px-1 py-5 border-b border-iv-line opacity-70 ${i % 2 === 0 ? "sm:border-r sm:pr-8" : "sm:pl-8"}`}>
                    <span className="mt-0.5 text-iv-fg-3">
                      <Icon size={16} strokeWidth={1.4} />
                    </span>
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-iv-fg-3">Planned</span>
                      <p className="text-[14px] text-iv-fg-2 leading-snug mt-1">{c.label}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-4 text-[12px] text-iv-fg-3 italic">
              Deletion requests today are handled manually via <a href="mailto:access@impactvest.com" className="iv-link-underline">access@impactvest.com</a>.
              Multi-user RBAC and automated retention pipelines will follow WP-INST and WP-J.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ LIVE-USE BAND */

function LiveUseBand() {
  return (
    <section
      id="pilot"
      data-testid={TESTIDS.sections.pilotBand}
      className="relative border-t border-iv-line py-32 md:py-44 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-x-0 top-0 h-[420px] pointer-events-none"
           style={{ background: "radial-gradient(600px 300px at 50% -60px, rgba(179,147,82,0.10), transparent 70%)" }} />
      <div className="iv-container relative">
        <div className="iv-eyebrow mb-10 justify-center flex">Live use · Focused first engagement</div>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif-display text-[46px] md:text-[72px] leading-[0.98] tracking-tight text-iv-fg">
            Begin with one live opportunity.
          </h2>
          <p className="text-[16px] md:text-[17.5px] leading-relaxed text-iv-fg-2 max-w-2xl mt-8 mx-auto">
            Use ImpactVest to bring the private evidence and relevant market context behind a live
            opportunity into one connected decision record.
          </p>
          <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-4">
            <Link
              to="/access?path=investor"
              data-testid="landing-begin-live-decision"
              className="iv-btn iv-btn--primary text-[15px] px-8 py-4"
              style={{ boxShadow: "0 24px 60px -18px rgba(14,90,62,0.55)" }}
            >
              Start analysis <ArrowRight size={16} />
            </Link>
            <Link to="/sample?path=investor" className="iv-btn text-[15px] px-8 py-4">
              Explore the sample opportunity
            </Link>
          </div>
          <p className="mt-6 font-mono text-[11px] tracking-[0.16em] uppercase text-iv-fg-3">
            Sample decision · Fictional data · Approval required only for live use
          </p>
        </div>
        <div className="mt-20 border-t border-iv-line pt-8 flex flex-wrap items-center justify-center gap-8 md:gap-12">
          <Trust icon={<ShieldCheck size={15} />} label="Private by design" />
          <Trust icon={<Link2 size={15} />} label="Evidence preserved" />
          <Trust icon={<UserCheck size={15} />} label="Human approved" />
          <Trust icon={<Shield size={15} />} label="Continuity carried forward" />
        </div>
      </div>
    </section>
  );
}

function Trust({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] uppercase text-iv-fg-3">
      <span className="text-iv-gold">{icon}</span>
      {label}
    </span>
  );
}
